(function (root) {
  'use strict';

  // Display-only thread shading. No stitch file, geometry, source or pricing mutation.
  const VERSION = '1.4.0';
  const metadata = new WeakMap();
  const MAX_PIXELS = 16777216;
  const MAX_SIDE = 8192;
  const TAU = Math.PI * 2;
  const QUALITIES = Object.freeze({ draft: 512, balanced: 768, high: 1024 });
  const MATERIALS = Object.freeze({
    textile: { grain: 0.8, sheen: 0.9, weave: 1.8 },
    cotton: { grain: 1, sheen: 0.8, weave: 1.5 },
    canvas: { grain: 1.15, sheen: 0.75, weave: 2.2 },
    knit: { grain: 1.15, sheen: 0.8, weave: 3.2 },
    fleece: { grain: 1.3, sheen: 0.65, weave: 2.6 },
    polyester: { grain: 0.65, sheen: 1.05, weave: 1.4 },
    linen: { grain: 1.1, sheen: 0.75, weave: 2 },
    wool: { grain: 1.2, sheen: 0.7, weave: 2.8 },
  });
  const MATERIAL_ALIASES = Object.freeze({ coton: 'cotton', toile: 'canvas', maille: 'knit', polaire: 'fleece', lin: 'linen', laine: 'wool' });
  const RIGID_MATERIAL = /^(?:metal|steel|stainless steel|aluminium|aluminum|ceramic|ceramique|glass|verre|wood|bois|plastic|plastique)$/;

  function clamp(value, low, high) { return Math.min(high, Math.max(low, value)); }
  function number(value, fallback, low, high) {
    return typeof value === 'number' && Number.isFinite(value) ? clamp(value, low, high) : fallback;
  }
  function token(value) {
    return typeof value === 'string' ? value.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
  }
  function aborted(signal) {
    if (signal && signal.aborted) throw new DOMException('Embroidery preview cancelled', 'AbortError');
  }
  function pause(signal) {
    return new Promise((resolve, reject) => {
      const timer = root.setTimeout(() => {
        if (signal && signal.removeEventListener) signal.removeEventListener('abort', cancel);
        try { aborted(signal); resolve(); } catch (error) { reject(error); }
      }, 0);
      function cancel() {
        root.clearTimeout(timer);
        if (signal.removeEventListener) signal.removeEventListener('abort', cancel);
        reject(new DOMException('Embroidery preview cancelled', 'AbortError'));
      }
      if (signal && signal.addEventListener) signal.addEventListener('abort', cancel, { once: true });
      if (signal && signal.aborted) cancel();
    });
  }
  function newCanvas(document, width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }
  function context(canvas) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Canvas 2D unavailable');
    return ctx;
  }
  function supportsTechnique(value) {
    // E is the exact Midocean embroidery code; callers must resolve supplier/config qualification first.
    return /^(?:embroidery|embroidery_3d|broderie|e)$/.test(token(value));
  }
  function substrateLuminance(value) {
    const hex = typeof value === 'string' ? value.trim().replace(/^#/, '') : '';
    const full = /^[0-9a-f]{3}$/i.test(hex) ? hex.replace(/./g, '$&$&') : hex;
    if (!/^[0-9a-f]{6}$/i.test(full)) return 0.5;
    return (parseInt(full.slice(0, 2), 16) * 0.2126 + parseInt(full.slice(2, 4), 16) * 0.7152 + parseInt(full.slice(4, 6), 16) * 0.0722) / 255;
  }
  function colorKey(r, g, b) {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    if (delta < 24 || delta / Math.max(1, max) < 0.16) return max > 190 ? 0 : max > 75 ? 1 : 2;
    let hue = max === r ? (g - b) / delta : max === g ? 2 + (b - r) / delta : 4 + (r - g) / delta;
    if (hue < 0) hue += 6;
    return 3 + Math.min(11, Math.floor(hue * 2));
  }
  function profile(material) {
    const name = token(material);
    const alias = Object.hasOwn(MATERIAL_ALIASES, name) ? MATERIAL_ALIASES[name] : name;
    return Object.hasOwn(MATERIALS, alias) ? MATERIALS[alias] : MATERIALS.textile;
  }

  async function render(sourceCanvas, options = {}) {
    const document = sourceCanvas && sourceCanvas.ownerDocument;
    const Canvas = document && document.defaultView && document.defaultView.HTMLCanvasElement;
    if (!Canvas || !(sourceCanvas instanceof Canvas)) throw new TypeError('Expected an HTMLCanvasElement');
    const input = options && typeof options === 'object' ? options : {};
    // The source, technique, light and settings describe one revision even if
    // the caller reuses its option object during this asynchronous render.
    const opts = { ...input, lighting: { ...input.lighting,
      direction: Array.isArray(input.lighting?.direction) ? [...input.lighting.direction] : undefined },
      qualification: input.qualification ? { ...input.qualification } : undefined };
    opts.analysis = input.analysis ? { ...input.analysis, classification: { ...input.analysis.classification } } : undefined;
    const signal = opts.signal;
    aborted(signal);
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;
    const technique = opts.techniqueId != null ? opts.techniqueId : opts.technique;
    // A distinct selected process enables raised shading. Arbitrary finish or
    // "puff" settings cannot upgrade ordinary embroidery to a 3D technique.
    const raised = token(technique) === 'embroidery_3d';
    if (width > MAX_SIDE || height > MAX_SIDE || width * height > MAX_PIXELS) throw new RangeError('Embroidery preview exceeds canvas budget');
    const output = newCanvas(document, width, height);
    const finish = (applied, reason) => {
      metadata.set(output, Object.freeze({ previewOnly: true, simulationOnly: true, applied, reason,
        process: raised ? 'embroidery_3d' : 'embroidery', mode: raised ? 'raised-thread-shading' : 'thread-shading', colorTreatment: 'effective-artwork-colors',
        geometryPreserved: true, effectiveArtworkUnchanged: true,
        limitations: Object.freeze(['simulation_only', 'display_thread_scale', 'no_stitch_program',
          'thread_palette_unverified', 'no_fabrication_approval', ...(raised ? ['raised-height-unverified', 'foam-and-stitch-program-unverified'] : []),
          ...(!applied ? [reason] : [])]) }));
      return output;
    };
    if (!width || !height) return finish(false, 'empty_source');
    const out = context(output);
    out.drawImage(sourceCanvas, 0, 0);
    // Snapshot before yielding so a caller reusing its canvas cannot change this job's artwork.
    await pause(signal);
    const strength = number(opts.strength, 0.82, 0, 1);
    // Qualification belongs to the existing selector. An explicit incompatible technique is never simulated.
    if (opts.qualified === false || opts.qualification?.compatible === false
      || (opts.qualification?.status != null && opts.qualification.status !== 'allowed')) return finish(false, 'qualification_rejected');
    // Analyse the effective file upstream. Thread shading cannot repair a gradient
    // or turn a pending source into production-ready embroidery.
    if (opts.analysis && (opts.analysis.status !== 'detected'
      || opts.analysis.classification?.gradients === 'present'
      || opts.analysis.classification?.kind === 'continuous_tone'
      || opts.analysis.classification?.verified === false)) return finish(false, 'embroidery_requires_flat_colors');
    if (!strength || (technique != null && !supportsTechnique(technique)) || RIGID_MATERIAL.test(token(opts.material))) return finish(false, 'visual_profile_unqualified');
    const material = profile(opts.material);
    const light = opts.lighting && typeof opts.lighting === 'object' ? opts.lighting : {};
    const direction = Array.isArray(light.direction) ? light.direction : [];
    const directionX = number(light.directionX, number(direction[0], -0.6, -1, 1), -1, 1);
    const directionY = number(light.directionY, number(direction[1], -0.8, -1, 1), -1, 1);
    const directionAngle = Math.atan2(directionY, directionX) * 180 / Math.PI;
    const azimuth = number(light.azimuth, directionAngle, -3600, 3600) * Math.PI / 180;
    const elevation = number(light.elevation, 50, 5, 90) * Math.PI / 180;
    const intensity = number(light.intensity, number(light.strength, 0.8, 0, 1), 0, 1);
    const lightX = Math.cos(azimuth);
    const lightY = Math.sin(azimuth);
    const luminance = substrateLuminance(opts.supportColor ?? opts.productColor ?? opts.substrateColor ?? opts.color);
    const quality = Object.hasOwn(QUALITIES, opts.quality) ? opts.quality : 'balanced';
    const ratio = Math.min(1, QUALITIES[quality] / Math.max(width, height));
    const workWidth = Math.max(1, Math.round(width * ratio));
    const workHeight = Math.max(1, Math.round(height * ratio));
    const working = newCanvas(document, workWidth, workHeight);
    const work = context(working);
    work.drawImage(output, 0, 0, workWidth, workHeight);
    let pixels;
    try { pixels = work.getImageData(0, 0, workWidth, workHeight).data; }
    catch (error) { if (error.name === 'SecurityError') return finish(false, 'pixels_unreadable'); throw error; }
    const keys = new Uint8Array(workWidth * workHeight);
    const regions = Array.from({ length: 15 }, () => ({ count: 0, x: 0, y: 0, xx: 0, yy: 0, xy: 0 }));
    let left = workWidth;
    let right = 0;
    for (let y = 0; y < workHeight; y += 1) {
      for (let x = 0; x < workWidth; x += 1) {
        const p = y * workWidth + x;
        const i = p * 4;
        if (pixels[i + 3] < 8) continue;
        const key = colorKey(pixels[i], pixels[i + 1], pixels[i + 2]);
        keys[p] = key;
        const region = regions[key];
        const weight = pixels[i + 3] / 255;
        region.count += weight;
        region.x += x * weight;
        region.y += y * weight;
        region.xx += x * x * weight;
        region.yy += y * y * weight;
        region.xy += x * y * weight;
        left = Math.min(left, x);
        right = Math.max(right, x);
      }
      if (y % 64 === 63) await pause(signal);
    }
    if (left > right) return finish(false, 'empty_artwork');
    const orientations = regions.map((region, key) => {
      const n = Math.max(1, region.count);
      const vx = region.xx / n - (region.x / n) ** 2;
      const vy = region.yy / n - (region.y / n) ** 2;
      const cov = region.xy / n - region.x * region.y / (n * n);
      const anisotropy = Math.hypot(vx - vy, 2 * cov) / Math.max(1, vx + vy);
      // Narrow areas use a satin-like transverse direction; broad fills share a steady angle.
      const angle = anisotropy > 0.65 ? Math.atan2(2 * cov, vx - vy) / 2 + Math.PI / 2 : (-35 + (key % 5 - 2) * 7) * Math.PI / 180;
      return { sin: Math.sin(angle), cos: Math.cos(angle) };
    });
    const displayWidth = number(opts.displayWidth, Math.min(width, 360), 1, 8192);
    const cssPerPixel = displayWidth / workWidth;
    // Keep each thread legible after the source canvas is reduced for display.
    // These are illustrative screen-space threads, not manufacturing dimensions.
    const pitch = Math.max(2.2, 1.65 / cssPerPixel);
    const length = Math.max(8, 6.5 / cssPerPixel);
    const visibleWidth = displayWidth * (right - left + 1) / workWidth;
    const scaleStrength = clamp(visibleWidth / 145, 0.16, 1);
    const textureStrength = strength * scaleStrength * (0.3 + intensity * 0.7);
    const reliefStrength = strength * scaleStrength * intensity * Math.cos(elevation) * (0.85 + luminance * 0.3);
    // Raised-thread relief is an indicative bevel within the exact silhouette.
    // It uses display pixels, never a claimed foam height or stitch density.
    const bevelRadius = Math.max(2, Math.min(12, Math.round(3 / cssPerPixel)));
    const threadRadius = Math.max(1, Math.min(6, Math.round(1.15 / cssPerPixel)));
    const shade = new Float32Array(workWidth * workHeight);
    function coverage(x, y, key) {
      if (x < 0 || y < 0 || x >= workWidth || y >= workHeight) return 0;
      const p = y * workWidth + x;
      return pixels[p * 4 + 3] / 255 * (keys[p] === key ? 1 : 0.68);
    }
    for (let y = 0; y < workHeight; y += 1) {
      for (let x = 0; x < workWidth; x += 1) {
        const p = y * workWidth + x;
        if (!pixels[p * 4 + 3]) continue;
        const key = keys[p];
        const orientation = orientations[key];
        const across = -orientation.sin * x + orientation.cos * y;
        const along = orientation.cos * x + orientation.sin * y;
        const row = Math.floor(across / pitch);
        // Stagger the needle entries between rows. A slight deterministic change
        // in stitch length avoids a uniform printed diagonal-line pattern.
        const rowVariation = Math.sin(row * 12.9898) * 0.045;
        const run = along / (length * (1 + rowVariation)) + row * 0.381966;
        const phase = run - Math.floor(run);
        const end = Math.abs(phase * 2 - 1) ** 22;
        const ridgePhase = across / pitch * TAU;
        const threadPhase = ridgePhase + Math.sin(along / length * TAU) * 0.1;
        const ridge = Math.cos(threadPhase);
        const flank = Math.sin(threadPhase) * (-orientation.sin * lightX + orientation.cos * lightY);
        const filament = Math.cos(threadPhase * 3 + along * 0.24) * 0.021;
        const grain = Math.sin(x * 1.71 + y * 2.39) * Math.sin(x * 0.63 - y * 0.87) * 0.012 * material.grain;
        const weave = Math.sin(x / material.weave) * Math.cos(y / material.weave) * 0.008;
        const nx = coverage(x - 1, y, key) - coverage(x + 1, y, key);
        const ny = coverage(x, y - 1, key) - coverage(x, y + 1, key);
        let edge = (nx * lightX + ny * lightY) * 0.22;
        // A rounded thread rim stays inside the exact alpha silhouette, including
        // counters in text. Classic embroidery gets a fine rim; 3D adds a wider bevel.
        for (let radius = 1; radius <= threadRadius; radius += 1) {
          const bx = coverage(x - radius, y, key) - coverage(x + radius, y, key);
          const by = coverage(x, y - radius, key) - coverage(x, y + radius, key);
          edge += (bx * lightX + by * lightY) * 0.42 / threadRadius;
        }
        edge *= reliefStrength;
        let raisedEdge = 0;
        if (raised) {
          for (let radius = 1; radius <= bevelRadius; radius += 1) {
            const bx = coverage(x - radius, y, key) - coverage(x + radius, y, key);
            const by = coverage(x, y - radius, key) - coverage(x, y + radius, key);
            raisedEdge += (bx * lightX + by * lightY) * (1 - radius / (bevelRadius + 1));
          }
          raisedEdge *= 0.65 * reliefStrength / bevelRadius;
        }
        const thread = (ridge * 0.29 + flank * 0.11) * material.sheen;
        const needle = end * (0.07 + Math.max(0, ridge) * 0.08);
        shade[p] = ((thread + filament + grain + weave - needle - 0.008) * textureStrength) + edge + raisedEdge;
      }
      if (y % 24 === 23) await pause(signal);
    }
    // Apply only luminance to original-resolution RGB. Alpha is copied byte-for-byte, including holes and fine lines.
    const image = out.getImageData(0, 0, width, height);
    const original = image.data;
    for (let y = 0; y < height; y += 1) {
      const wy = Math.min(workHeight - 1, (y + 0.5) * workHeight / height - 0.5);
      const y0 = Math.max(0, Math.floor(wy));
      const y1 = Math.min(workHeight - 1, y0 + 1);
      const fy = Math.max(0, wy - y0);
      for (let x = 0; x < width; x += 1) {
        const i = (y * width + x) * 4;
        if (!original[i + 3]) continue;
        const wx = Math.min(workWidth - 1, (x + 0.5) * workWidth / width - 0.5);
        const x0 = Math.max(0, Math.floor(wx));
        const x1 = Math.min(workWidth - 1, x0 + 1);
        const fx = Math.max(0, wx - x0);
        const upper = shade[y0 * workWidth + x0] * (1 - fx) + shade[y0 * workWidth + x1] * fx;
        const lower = shade[y1 * workWidth + x0] * (1 - fx) + shade[y1 * workWidth + x1] * fx;
        const value = clamp(upper * (1 - fy) + lower * fy, -0.24, 0.2);
        for (let c = 0; c < 3; c += 1) {
          original[i + c] = value >= 0 ? original[i + c] + (255 - original[i + c]) * value * 0.78 : original[i + c] * (1 + value);
        }
      }
      if (y % 24 === 23) await pause(signal);
    }
    aborted(signal);
    out.putImageData(image, 0, 0);
    return finish(true, 'simulated');
  }

  root.TodayMerchPreviewRenderers = root.TodayMerchPreviewRenderers || {};
  if (root.TodayMerchPreviewRenderers.embroidery) return;
  root.TodayMerchPreviewRenderers.embroidery = Object.freeze({ version: VERSION, render, supportsTechnique,
    getMetadata: canvas => metadata.get(canvas) || null });
})(window);
